/**
 * One message template (one message, one channel, one locale): the approved
 * builder workspace bound to the real API.
 *
 * Email: Message settings (subject, preheader, shared layout), then the
 * EmailBuilder on the body's HTML + Liquid (Editor / Preview / HTML views, block
 * inspector, styles, placeholders, undo / redo, zoom, device and light / dark
 * preview, layout header / footer switches, preview data). SMS: plain text with
 * the server's character / segment count and a phone preview.
 *
 * What the server decides is shown as the server says it: what this row can
 * change (`authoring` + reasons), the render (preview), what blocks publishing
 * (validation), and every refused write. Unsaved edits are kept in memory per
 * row and survive switching rows, channels and tabs.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Banner, Btn, ConfirmDialog, Disclosure, Dropdown, Field, Input, PanelHeader, SegmentedControl, Skeleton,
  SourceEditor, Textarea, Toggle, focusSourceEditorRange, getSourceEditor, type PreviewDevice,
} from "@v0y4ge/jrni-ui";
import { EmailBuilder, VIEWPORT, type EmailBuilderView, type SourceField } from "@v0y4ge/jrni-email-builder";
import type { MessageTemplateDetail } from "@v0y4ge/api-contracts";
import { builderApi, failureIssues, type BuilderIssue, type Failure } from "../api";
import { outcomeUnknown } from "../commands";
import { tokenContextFor, useTokens, type LayoutLibrary } from "../data";
import {
  bindingCopy, bindingFromValue, bindingValue, channelName, effectiveLayout, layoutLabel, layoutOptions,
  offsetOf, problemsTitle, publishConsequence, templateChips, versionWhen,
  type CatalogueMessage, type EmailWork, type SmsWork, type Tr, type Work, workOf,
} from "../model";
import { BODY_BUILDER_ID, cancelBuilderEdit, gateBuilder, insertIntoBuilder, useBodyBuilder } from "../useBodyBuilder";
import { useTemplatePreview, useTemplateValidation } from "../usePreview";
import type { TemplateStore } from "../useTemplates";
import { BuilderActionBar, type BarBusy } from "./BuilderActionBar";
import { Chips, EditorSkeleton, FailureBanner, FooterSkeleton, PlaceholderPicker, PreviewSkeleton, SmsSkeleton, useBuilderFrame, usePlaceholderIconOnly } from "./common";
import { HistoryModal, type HistoryRow } from "./HistoryModal";
import { PreviewDataPanel, type PreviewSource } from "./PreviewDataPanel";

const SRC_ID = "msgb-body-source";
const SMS_ID = "msgb-sms-text";
/** The email builder views offered here: the JSON view stays off (approved). */
const TEMPLATE_VIEWS: EmailBuilderView[] = ["editor", "preview", "source"]; // ui-config
const SUBJECT_ID = "msgb-subject";
const PRE_ID = "msgb-preheader";

type Target = { kind: "subject" | "preheader" | "sms"; start: number; end: number } | { kind: "source" } | { kind: "canvas" } | null;
interface Notice { tone: "info" | "warning" | "danger" | "success" | "neutral"; title: string; text: ReactNode }

export interface TemplateEditorProps {
  t: Tr;
  store: TemplateStore;
  message: CatalogueMessage;
  channel: "email" | "sms";
  onChannel: (channel: "email" | "sms") => void;
  library: LayoutLibrary | null;
  canWrite: boolean;
  onOpenLayout: (layoutKey: string) => void;
  device: PreviewDevice;
  onDevice: (d: PreviewDevice) => void;
  /** The catalogue changed (a publish or discard moved a row's state). */
  onRowChanged: () => void;
  /** Tablet and phone: the "Show list" button that opens the rail Drawer, shown first in the header row. */
  railTrigger?: ReactNode;
}

export function TemplateEditor(p: TemplateEditorProps) {
  const { t, store, message, channel } = p;
  const row = message.rows[channel];
  const templateId = row ? row.templateId : null;

  useEffect(() => { if (templateId) store.load(templateId); }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchChannel = (next: "email" | "sms") => {
    if (next === channel) return;
    const g = gateBuilder(BODY_BUILDER_ID);
    if (!g.ok) return; // the builder keeps the typed value and shows why
    p.onChannel(next);
  };

  const ds = templateId ? store.detailOf(templateId) : undefined;
  /** One header row: list trigger (tablet/phone), name + state chips, group · last change, and the channel switch.
   *  It stays while a row or channel loads, so the title and the channel never vanish; while the row loads, a
   *  placeholder holds the last-change line's place. */
  const renderHeader = (readOnly: boolean, metaLoading = false) => (
    <div className="msgb-edhead">
      {p.railTrigger}
      <PanelHeader as="h2" size="md" divider={false}
        title={<span className="msgb-titlerow"><span className="msgb-ellipsis">{message.name}</span>{row ? <Chips chips={[...templateChips(row, t),
          ...(readOnly ? [{ tone: "neutral" as const, label: t("messaging.builder.readOnly", "Read only") }] : [])]} /> : null}</span>}
        description={metaLoading ? (
          <span className="msgb-metaline">
            <span className="msgb-ellipsis">{`${message.groupLabel} ·`}</span>
            <span className="msgb-metaskel"><Skeleton variant="text" width={200} height={12} /></span>
          </span>
        ) : <span className="msgb-ellipsis">{[message.groupLabel, ds && ds.phase === "ready" ? lastChange(ds.detail, t) : null].filter(Boolean).join(" · ")}</span>}
        actions={<SegmentedControl<"email" | "sms"> size="sm" ariaLabel={t("messaging.builder.channel", "Channel")} value={channel} onChange={switchChannel} testid="msgb-channel"
          options={[
            { value: "email", label: t("messaging.builder.channel.email", "Email"), disabled: !message.rows.email },
            { value: "sms", label: t("messaging.builder.channel.sms", "SMS"), disabled: !message.rows.sms },
          ]} />} />
    </div>
  );

  if (!templateId) {
    return <div className="msgb-split">{renderHeader(!p.canWrite)}<div className="msgb-edbody">
      <Banner tone="neutral" size="compact" title={t("messaging.builder.noChannel", "This message has no template for this channel")}>
        {t("messaging.builder.noChannelText", "Choose the other channel.")}
      </Banner></div></div>;
  }
  if (!ds || ds.phase === "loading") {
    // Shaped like the channel being opened: the SMS editor is a text field and a phone preview, not the email builder.
    const loading = t("messaging.builder.loading.template", "Loading message template");
    return <>
      <div className="msgb-split">{renderHeader(!p.canWrite, true)}{channel === "sms" ? <SmsSkeleton label={loading} /> : <EditorSkeleton withHeader={false} label={loading} />}</div>
      <FooterSkeleton />
    </>;
  }
  if (ds.phase === "error") {
    return <div className="msgb-split">{renderHeader(!p.canWrite)}<div className="msgb-edbody"><FailureBanner t={t} failure={ds.failure} onRetry={() => store.load(templateId, true)} /></div></div>;
  }
  return <ReadyEditor {...p} templateId={templateId} detail={ds.detail} refreshing={ds.refreshing} renderHeader={renderHeader} />;
}

function lastChange(d: MessageTemplateDetail, t: Tr): string {
  if (d.draft) return `${t("messaging.builder.draftSaved", "Draft saved")} ${versionWhen(d.draft.updatedAt)}${d.published ? ` · ${t("messaging.builder.sendingVersion", "sending version")} ${d.published.version}` : ""}`;
  if (d.published) return `${t("messaging.builder.publishedOn", "Published")} ${versionWhen(d.published.publishedAt)} · ${t("messaging.builder.version", "version")} ${d.published.version}`;
  return t("messaging.builder.notPublished", "Not published yet. It sends nothing until it is published.");
}

function ReadyEditor(p: TemplateEditorProps & { templateId: string; detail: MessageTemplateDetail; refreshing: boolean; renderHeader: (readOnly: boolean, metaLoading?: boolean) => ReactNode }) {
  const { t, store, templateId, detail, message } = p;
  const authoring = detail.authoring;
  const work = (store.workOf(templateId) || workOf(null, detail.channel)) as Work;
  const dirty = store.dirty(templateId);
  const busy = store.busy(templateId);
  const tokens = useTokens(tokenContextFor(message.groupId));
  const tokenData = tokens.phase === "ready" || tokens.data ? tokens.data : null;
  const canEditBody = p.canWrite && (detail.channel === "email" ? authoring.html : authoring.smsText);
  const readOnlyReason = !p.canWrite
    ? t("messaging.builder.readOnly.permission", "You can view this message but not change it.")
    : !canEditBody ? (authoring.unsupportedReasons[detail.channel === "email" ? "html" : "smsText"] || t("messaging.builder.readOnly.row", "This message can't be edited here.")) : null;

  const [notice, setNotice] = useState<Notice | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"publish" | "discardChanges" | "discardDraft" | "history" | null>(null);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<PreviewSource>({ kind: "samples" });
  const [sourceOpen, setSourceOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean | null>(null);
  const target = useRef<Target>(null);

  // A different row: drop row-scoped notices (edits are per row in the store and stay).
  useEffect(() => { setNotice(null); setFailure(null); setConfirmation(null); setDialog(null); setPreview({ kind: "samples" }); target.current = null; }, [templateId]);

  const setWork = (next: Work) => { setConfirmation(null); store.setWork(templateId, next); };
  const fields: SourceField[] = useMemo(() => (tokenData ? tokenData.tokens.map((tk) => ({ id: tk.id, label: tk.label, link: tk.type === "link" })) : []), [tokenData]);
  const samples = useMemo(() => (tokenData ? tokenData.samples : {}), [tokenData]);

  const previewRead = useTemplatePreview(templateId, work, preview.kind === "record" ? preview.record : null, true);
  const validation = useTemplateValidation(templateId, work, p.canWrite);
  const pv = previewRead.phase === "ready" || (previewRead.phase === "error" && previewRead.data) ? (previewRead.phase === "ready" ? previewRead.data : previewRead.data) : null;
  const val = validation.phase === "ready" ? validation.data : validation.phase === "error" ? validation.data : null;

  const issues: BuilderIssue[] = [...(val ? val.issues : []), ...(failure && (failure.kind === "invalid" || failure.kind === "unsupported") ? failureIssues(failure) : [])];
  const blocking = val && !val.publishable && !(validation.phase === "ready" && validation.stale);
  const publishBlocked = !p.canWrite ? t("messaging.builder.readOnly.permission", "You can view this message but not change it.")
    : blocking ? problemsTitle(val ? val.issues.length : 0, t) : null;

  /* ---------- placeholders ---------- */
  const placeholderOptions = useMemo(() => {
    if (!tokenData) return [];
    const ok = tokenData.tokens.map((tk) => ({ value: tk.id, label: tk.label, supportingText: `{{ ${tk.id} }}` }));
    const off = tokenData.unavailable.map((tk) => ({ value: `__off__${tk.id}`, label: tk.label, disabled: true,
      supportingText: (tk as { reason?: string }).reason || t("messaging.builder.tokenUnavailable", "Not available for this message") }));
    return [...ok, ...off];
  }, [tokenData, t]);
  const insertText = (raw: string): boolean => {
    const tg = target.current;
    if (!tg) return false;
    if (tg.kind === "canvas") return insertIntoBuilder(BODY_BUILDER_ID, raw);
    if (tg.kind === "source" && work.channel === "email") {
      const ed = getSourceEditor(SRC_ID); if (!ed) return false;
      const sel = ed.getSelection(); const html = ed.getValue();
      setWork({ ...work, html: html.slice(0, sel.from) + raw + html.slice(sel.to) });
      setTimeout(() => focusSourceEditorRange(SRC_ID, sel.from + raw.length), 0);
      return true;
    }
    if ((tg.kind === "subject" || tg.kind === "preheader") && work.channel === "email") {
      const v = work[tg.kind]; const next = v.slice(0, tg.start) + raw + v.slice(tg.end);
      setWork({ ...work, [tg.kind]: next });
      target.current = { ...tg, start: tg.start + raw.length, end: tg.start + raw.length };
      return true;
    }
    if (tg.kind === "sms" && work.channel === "sms") {
      const next = work.text.slice(0, tg.start) + raw + work.text.slice(tg.end);
      setWork({ ...work, text: next });
      target.current = { ...tg, start: tg.start + raw.length, end: tg.start + raw.length };
      return true;
    }
    return false;
  };
  const onPlaceholder = (id: string) => {
    if (!id || id.startsWith("__off__")) return;
    if (!insertText(`{{ ${id} }}`)) {
      setNotice({ tone: "info", title: t("messaging.builder.placeholder.where", "Choose where the placeholder goes"),
        text: work.channel === "sms" ? t("messaging.builder.placeholder.whereSms", "Click into the SMS text first, then choose a placeholder.")
          : t("messaging.builder.placeholder.whereEmail", "Click into the subject, preheader, a text field or the HTML source first, then choose a placeholder.") });
    }
  };
  const caret = (kind: "subject" | "preheader" | "sms") => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
    const el = e.currentTarget; target.current = { kind, start: el.selectionStart ?? el.value.length, end: el.selectionEnd ?? el.value.length };
  };
  const placeholderPicker = (disabled: boolean, iconOnly = false) => (
    <PlaceholderPicker t={t} options={placeholderOptions} onPick={onPlaceholder} disabled={disabled} iconOnly={iconOnly} />
  );

  /* ---------- commands ---------- */
  const gate = (): boolean => {
    const g = gateBuilder(BODY_BUILDER_ID);
    if (!g.ok) {
      setNotice({ tone: "warning", title: t("messaging.builder.finishField", "Finish or undo the field edit first"),
        text: `${g.reason} ${t("messaging.builder.finishFieldText", "Your typed value is still in the field; nothing else changed.")}` });
    }
    return g.ok;
  };
  /** The action a "Try again" repeats (same intent, so the same idempotency key while the request is unchanged). */
  const lastAction = useRef<"save" | "publish" | "discardDraft">("save");
  const save = async () => {
    if (!gate()) return;
    lastAction.current = "save";
    setFailure(null);
    const r = await store.save(templateId);
    if (r.ok) setConfirmation(t("messaging.builder.saved", "Draft saved. Nothing sends until you publish."));
    else setFailure(r.failure);
    p.onRowChanged();
  };
  const publish = async () => {
    lastAction.current = "publish";
    setFailure(null);
    const r = await store.publish(templateId, note.trim() || undefined);
    setDialog(null);
    if (r.ok) {
      setNote("");
      const pub = r.value.publication;
      setConfirmation(`${t("messaging.builder.published", "Published version")} ${pub.publishedVersion}.`);
      setNotice({ tone: "success", title: `${t("messaging.builder.published", "Published version")} ${pub.publishedVersion}`,
        text: <>{publishConsequence(t)}{pub.ownershipTransferred ? ` ${t("messaging.builder.ownership", "This message is now maintained by your team. The platform's original stays in Version history.")}` : ""}</> });
    } else setFailure(r.failure);
    p.onRowChanged();
  };
  const discardDraft = async () => {
    lastAction.current = "discardDraft";
    setFailure(null);
    const r = await store.discardDraft(templateId);
    setDialog(null);
    if (r.ok) { cancelBuilderEdit(BODY_BUILDER_ID); setConfirmation(t("messaging.builder.draftDiscarded", "Draft discarded. The published version keeps sending.")); }
    else setFailure(r.failure);
    p.onRowChanged();
  };

  const barBusy: BarBusy = busy === "discard" ? "discardDraft" : busy;
  // A render function: it reads the conflict state defined further down.
  const renderBar = () => (
    <BuilderActionBar t={t} testid="msgb-template-bar" dirty={dirty} busy={barBusy} readOnly={!p.canWrite} canEditContent={!!canEditBody}
      hasServerDraft={!!detail.draft} publishBlocked={publishBlocked} blocked={strandedReason}
      note={readOnlyReason || strandedReason || (publishBlocked && p.canWrite ? publishBlocked : t("messaging.builder.saveNote", "Saving a draft never changes what sends. Publishing applies from the next message prepared."))}
      confirmation={confirmation}
      failure={failure && failure.kind !== "conflict" ? <FailureBanner t={t} failure={failure} onDismiss={() => setFailure(null)}
        onRetry={outcomeUnknown(failure) ? () => { if (lastAction.current === "publish") void publish(); else if (lastAction.current === "discardDraft") void discardDraft(); else void save(); } : undefined} /> : null}
      onSave={() => void save()}
      onDiscardChanges={() => setDialog("discardChanges")}
      onDiscardDraft={() => { if (gate()) setDialog("discardDraft"); }}
      onPublish={() => { if (gate()) setDialog("publish"); }}
      onHistory={() => { if (gate()) setDialog("history"); }} />
  );

  // Validation still runs; its banner is shown only to someone who can act on it.
  const issuesBanner = issues.length && canEditBody ? (
    <Banner tone={blocking ? "danger" : "warning"} size="compact" title={blocking ? problemsTitle(issues.length, t) : t("messaging.builder.check", "Check before publishing")}>
      <ul className="msgb-problems">
        {issues.map((iss, i) => (
          <li key={`${iss.path}|${iss.code}|${i}`}>
            {iss.message}{iss.line ? ` (${t("messaging.builder.line", "line")} ${iss.line})` : ""}
            {iss.line && iss.path.endsWith(".html") && work.channel === "email" ? (
              <> <Btn variant="link" size="sm" onClick={() => { setSourceOpen(true); setTimeout(() => focusSourceEditorRange(SRC_ID, offsetOf(work.html, iss.line as number, iss.column || 1)), 0); }}>
                {t("messaging.builder.showInHtml", "Show in HTML")}</Btn></>
            ) : null}
          </li>
        ))}
      </ul>
    </Banner>
  ) : null;

  const warnings = store.warnings(templateId);
  const conflict = store.conflictState(templateId);
  const sharedSource = detail.authoringRevision != null;
  const strandedReason = conflict.stranded
    ? t("messaging.builder.stranded.blocked", "Saving and publishing are paused for this draft. Copy your content out first.")
    : null;
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);
  const contentText = work.channel === "sms" ? work.text
    : `${t("messaging.builder.subject", "Subject")}: ${work.subject}\n${work.preheader ? `${t("messaging.builder.preheader", "Preheader")}: ${work.preheader}\n` : ""}\n${work.html}`;
  const copyOut = () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) { setCopied("fail"); return; }
    navigator.clipboard.writeText(contentText).then(() => setCopied("ok"), () => setCopied("fail"));
  };
  const downloadOut = () => {
    const blob = new Blob([contentText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${detail.messageId.replace(/[^A-Za-z0-9_.-]/g, "_")}-${detail.locale || "default"}-${detail.channel}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const conflictBanner = conflict.stranded ? (
    <Banner tone="danger" size="compact" title={t("messaging.builder.stranded.title", "This draft can't be saved or published right now")} testid="msgb-stranded">
      {sharedSource
        ? t("messaging.builder.stranded.shared", "The shared appointment message this draft was started from was changed elsewhere after the draft was created, so the server refuses changes to this draft. Your content is still here, on this screen only. Copy it out before you leave.")
        : t("messaging.builder.stranded.generic", "The server keeps refusing changes to this draft even after reloading it. Your content is still here, on this screen only. Copy it out before you leave.")}
      {" "}{t("messaging.builder.stranded.discard", "To start again from the published version, discard your changes, then discard the draft. If the server refuses that as well, it says why here.")}
      <div className="msgb-actions" style={{ marginTop: "var(--jrni-space-2)" }}>
        <Btn size="sm" variant="secondary" onClick={copyOut}>{t("messaging.builder.copyContent", "Copy content")}</Btn>
        <Btn size="sm" variant="secondary" onClick={downloadOut}>{t("messaging.builder.downloadContent", "Download as text")}</Btn>
        <Btn size="sm" variant="ghost" onClick={() => { setFailure(null); store.retryFromServer(templateId); }}>{t("messaging.builder.tryFromServer", "Reload and try again")}</Btn>
        <span className="msgb-small" role="status">{copied === "ok" ? t("messaging.builder.copied", "Copied.") : copied === "fail" ? t("messaging.builder.copyFailed", "Couldn't copy automatically. Use Download instead.") : ""}</span>
      </div>
    </Banner>
  ) : conflict.count === 1 && failure && failure.kind === "conflict" ? (
    <Banner tone="warning" size="compact" title={t("messaging.builder.conflict.title", "This message changed elsewhere")} onDismiss={() => setFailure(null)} testid="msgb-conflict">
      {t("messaging.builder.conflict.text", "It was reloaded with what is saved now. Your edits are still here; save again to apply them.")}
      {sharedSource ? ` ${t("messaging.builder.conflict.shared", "This can also happen when the shared appointment message was edited elsewhere, even if the published version looks the same.")}` : ""}
    </Banner>
  ) : null;
  const common = (
    <>
      {p.refreshing ? <span className="msgb-sr" role="status">{t("messaging.builder.refreshing", "Refreshing")}</span> : null}
      {conflictBanner}
      {notice ? <Banner tone={notice.tone} size="compact" title={notice.title} onDismiss={() => setNotice(null)}>{notice.text}</Banner> : null}
      {warnings.length ? (
        <Banner tone="warning" size="compact" title={t("messaging.builder.notApplied", "Part of the change was not applied")}>
          <ul className="msgb-problems">{warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
        </Banner>
      ) : null}
      {tokens.phase === "error" && tokens.failure && tokens.failure.kind === "forbidden" ? (
        <Banner tone="neutral" size="compact" title={t("messaging.builder.tokensForbidden", "The placeholder list isn't available with your access")}>
          {t("messaging.builder.tokensForbiddenText", "Placeholders already in this message keep working, and the server still checks them when you publish.")}
        </Banner>
      ) : tokens.phase === "error" && tokens.failure ? (
        <FailureBanner t={t} failure={tokens.failure} onRetry={tokens.reload}>
          {" "}{t("messaging.builder.tokensFailed", "Placeholders can't be listed until this loads; the server still checks them on publish.")}
        </FailureBanner>
      ) : null}
    </>
  );

  return (
    <>
      <div className="msgb-split">
        {p.renderHeader(!!readOnlyReason)}
        {work.channel === "email"
          ? <EmailPane {...p} lead={common} work={work} setWork={setWork} canEdit={!!canEditBody} fields={fields} samples={samples}
              serverHtml={pv && pv.email ? pv.email.html : null} preview={preview} setPreview={setPreview}
              rendered={pv ? { values: pv.values, missingTokens: pv.missingTokens, unknownTokens: pv.unknownTokens } : null}
              rendering={previewRead.phase === "loading" || (previewRead.phase === "ready" && previewRead.stale)}
              previewFailure={previewRead.phase === "error" ? previewRead.failure : null} onPreviewRetry={previewRead.retry}
              issuesBanner={issuesBanner} diagnostics={issues} placeholderPicker={placeholderPicker}
              target={target} caret={caret} sourceOpen={sourceOpen} setSourceOpen={setSourceOpen}
              settingsOpen={settingsOpen} setSettingsOpen={setSettingsOpen} insertSnippet={(s) => insertText(s)}
              tokenSnippets={tokenData ? tokenData.tokens.filter((tk) => tk.blankable) : []} />
          : <SmsPane {...p} lead={common} work={work} setWork={setWork} canEdit={!!canEditBody} issuesBanner={issuesBanner}
              sms={pv && pv.sms ? pv.sms : null} rendering={previewRead.phase === "loading" || (previewRead.phase === "ready" && previewRead.stale)}
              previewFailure={previewRead.phase === "error" ? previewRead.failure : null} onPreviewRetry={previewRead.retry}
              placeholderPicker={placeholderPicker} caret={caret}
              preview={preview} setPreview={setPreview}
              rendered={pv ? { values: pv.values, missingTokens: pv.missingTokens, unknownTokens: pv.unknownTokens } : null} />}
      </div>
      {renderBar()}

      <ConfirmDialog open={dialog === "publish"} tone="warning" busy={busy === "publish" || busy === "save"}
        title={`${t("messaging.builder.publishTitle", "Publish")} ${message.name} (${channelName(detail.channel, t)})?`}
        body={publishConsequence(t)}
        consequence={detail.provenance.origin === "seed" && !detail.provenance.ownershipTransferredAt
          ? t("messaging.builder.publish.takeOver", "This replaces the platform's default content for this message with yours. The platform's version stays in Version history.")
          : dirty ? t("messaging.builder.publish.savesFirst", "Your unsaved changes are saved as the draft first, then published.") : undefined}
        confirmLabel={t("messaging.builder.publish", "Publish")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={() => void publish()} testid="msgb-publish-confirm">
        <Field label={t("messaging.builder.versionNote", "Version note (optional)")} htmlFor="msgb-publish-note" hint={t("messaging.builder.versionNoteHint", "Shown in Version history.")}>
          <Input id="msgb-publish-note" full value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </ConfirmDialog>
      <ConfirmDialog open={dialog === "discardChanges"} tone="neutral" title={t("messaging.builder.discardTitle", "Discard unsaved changes?")}
        body={t("messaging.builder.discardChangesBody", "Your edits since the last save are removed. The saved draft and the published version are not changed.")}
        confirmLabel={t("messaging.builder.discard", "Discard changes")} cancelLabel={t("messaging.builder.keepEditing", "Keep editing")}
        onCancel={() => setDialog(null)} onConfirm={() => { cancelBuilderEdit(BODY_BUILDER_ID); store.discardLocal(templateId); setDialog(null); }} testid="msgb-discard-confirm" />
      <ConfirmDialog open={dialog === "discardDraft"} tone="warning" busy={busy === "discard"} title={t("messaging.builder.discardDraftTitle", "Discard the saved draft?")}
        body={t("messaging.builder.discardDraftBody", "The draft and its unpublished changes are removed.")}
        consequence={detail.published ? t("messaging.builder.discardDraftKeeps", "The published version keeps sending.") : t("messaging.builder.discardDraftNone", "This message has no published version, so it still sends nothing.")}
        confirmLabel={t("messaging.builder.discardDraft", "Discard draft")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={() => void discardDraft()} testid="msgb-discard-draft-confirm" />
      <HistoryModal t={t} open={dialog === "history"} onClose={() => setDialog(null)}
        subject={`${message.name} · ${channelName(detail.channel, t)}`}
        provenance={<TemplateProvenance t={t} detail={detail} />}
        load={async () => {
          const res = await builderApi.listMessageTemplateVersions(templateId, { limit: 100 });
          return res.data.map((v): HistoryRow => ({ version: v.version, state: v.state, origin: v.origin, createdAt: v.createdAt, createdBy: v.createdBy, note: v.note, publishedAt: v.publishedAt }));
        }}
        onRestore={p.canWrite && canEditBody ? async (version) => {
          if (!gate()) return t("messaging.builder.finishField", "Finish or undo the field edit first");
          try {
            const res = await builderApi.getMessageTemplateVersion(templateId, version);
            const restored = workOf(res.data.content, detail.channel);
            setWork(restored);
            setNotice({ tone: "info", title: `${t("messaging.builder.history.loaded", "Version")} ${version} ${t("messaging.builder.history.loadedSuffix", "is in the editor")}`,
              text: t("messaging.builder.history.loadedText", "It is not saved yet. Save a draft to keep it, then publish to send it.") });
            return null;
          } catch (e) {
            return t("messaging.builder.history.loadFailed", "That version couldn't be loaded. Nothing was changed.");
          }
        } : null} />
    </>
  );
}

function TemplateProvenance({ t, detail }: { t: Tr; detail: MessageTemplateDetail }) {
  const pr = detail.provenance;
  const lines: string[] = [];
  lines.push(pr.origin === "seed" ? t("messaging.builder.prov.seed", "Started from the platform's default content.") : t("messaging.builder.prov.authored", "Written by your team."));
  if (pr.ownershipTransferredAt) lines.push(`${t("messaging.builder.prov.transferred", "Taken over from the platform default")} ${versionWhen(pr.ownershipTransferredAt)}${pr.ownershipTransferredBy ? ` ${t("messaging.builder.prov.by", "by")} ${pr.ownershipTransferredBy}` : ""}.`);
  if (pr.updatedAt) lines.push(`${t("messaging.builder.prov.updated", "Last changed")} ${versionWhen(pr.updatedAt)}${pr.updatedBy ? ` ${t("messaging.builder.prov.by", "by")} ${pr.updatedBy}` : ""}.`);
  return <div className="msgb-meta">{lines.map((l) => <span key={l}>{l}</span>)}</div>;
}

/* ======================= email ======================= */

interface EmailPaneProps extends TemplateEditorProps {
  /** Row notices shown first in the settings strip (conflict, notices, warnings, placeholder access). */
  lead: ReactNode;
  templateId: string;
  detail: MessageTemplateDetail;
  work: EmailWork;
  setWork: (w: Work) => void;
  canEdit: boolean;
  fields: SourceField[];
  samples: Readonly<Record<string, string>>;
  serverHtml: string | null;
  preview: PreviewSource;
  setPreview: (s: PreviewSource) => void;
  rendered: { values: "catalog_samples" | "record"; missingTokens: string[]; unknownTokens: string[] } | null;
  rendering: boolean;
  previewFailure: Failure | null;
  onPreviewRetry: () => void;
  issuesBanner: ReactNode;
  diagnostics: BuilderIssue[];
  placeholderPicker: (disabled: boolean, iconOnly?: boolean) => ReactNode;
  target: { current: Target };
  caret: (kind: "subject" | "preheader" | "sms") => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => void;
  sourceOpen: boolean;
  setSourceOpen: (o: boolean) => void;
  settingsOpen: boolean | null;
  setSettingsOpen: (o: boolean) => void;
  insertSnippet: (text: string) => boolean;
  tokenSnippets: { id: string; label: string }[];
}

function EmailPane(p: EmailPaneProps) {
  const { t, work, detail, library } = p;
  const authoring = detail.authoring;
  const layouts = library ? library.layouts : [];
  const def = library ? library.defaultLayout : null;
  const eff = effectiveLayout(layouts, def, work.layout);
  const binding = bindingCopy(eff, work.layout, t);

  // The published source of the layout this email is placed into (for the canvas).
  const [layoutHtml, setLayoutHtml] = useState<{ key: string; version: number; html: string | null } | null>(null);
  useEffect(() => {
    if (!eff || !eff.published || eff.archived) { setLayoutHtml(null); return; }
    const key = eff.layoutKey; const version = eff.published.version;
    if (layoutHtml && layoutHtml.key === key && layoutHtml.version === version) return;
    let live = true;
    builderApi.getMessageLayout(key).then((res) => { if (live) setLayoutHtml({ key, version, html: res.data.publishedHtml }); }, () => { if (live) setLayoutHtml({ key, version, html: null }); });
    return () => { live = false; };
  }, [eff && eff.layoutKey, eff && eff.published ? eff.published.version : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = useBodyBuilder({
    templateId: p.templateId,
    title: p.message.name,
    html: work.html,
    setHtml: (html) => p.setWork({ ...work, html }),
    layoutHtml: layoutHtml && layoutHtml.html ? layoutHtml.html : null,
    layoutName: eff ? layoutLabel(eff, t) : t("messaging.builder.layout.none", "No layout"),
    show: work.sections,
    samples: p.samples,
    fields: p.fields,
    serverPreviewHtml: p.serverHtml,
    readOnly: !p.canEdit,
  });

  const sectionSupport = eff && eff.sections ? eff.sections : null;
  const sectionToggle = (which: "header" | "footer") => {
    const support = sectionSupport ? sectionSupport[which] : null;
    const on = work.sections[which];
    const label = which === "header" ? t("messaging.builder.sections.header", "Show the layout's header") : t("messaging.builder.sections.footer", "Show the layout's footer");
    const why = support === "absent" ? t("messaging.builder.sections.absent", "This layout has none.")
      : support === "unsupported" ? (sectionSupport && sectionSupport.reason) || t("messaging.builder.sections.unsupported", "This layout can't leave it out exactly, so it can only be shown.")
      : null;
    return (
      <Toggle key={which} label={label} checked={on} size="sm"
        supportingText={why || undefined}
        disabled={!p.canEdit || !authoring.sections || support === "absent" || (support === "unsupported" && on)}
        onChange={(checked: boolean) => p.setWork({ ...work, sections: { ...work.sections, [which]: checked } })} />
    );
  };
  const sectionsExtra = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
      {authoring.sections ? <>{sectionToggle("header")}{sectionToggle("footer")}</> : (
        <p className="msgb-small">{authoring.unsupportedReasons.sections || t("messaging.builder.sections.notHere", "This message always shows the layout's header and footer.")}</p>
      )}
      {body.sectionError ? <p className="msgb-fielderr" role="alert">{t("messaging.builder.sections.error", "This layout can't hide that section exactly. Turn it back on, or choose another layout; the email can't publish like this.")}</p> : null}
    </div>
  );

  const linkOptions = useMemo(() => p.fields.filter((f) => f.link).map((f) => ({ value: String(f.id), label: String(f.label) })), [p.fields]);
  const snippetOptions = p.tokenSnippets.map((tk) => ({ value: tk.id, label: `${t("messaging.builder.logic.ifKnown", "If known:")} ${tk.label}` }));
  const insertLogic = (id: string) => {
    if (!id) return;
    // ui-config: a structural Liquid snippet over one catalogue token; the author writes the text.
    const text = `\n{% if ${id} %}\n<p>{{ ${id} }}</p>\n{% else %}\n<p></p>\n{% endif %}\n`;
    if (!(p.target.current && p.target.current.kind === "source" && p.insertSnippet(text))) {
      p.setWork({ ...work, html: work.html + text });
    }
  };

  const settingsDefault = p.settingsOpen == null ? false : p.settingsOpen;
  const frame = useBuilderFrame();
  const placeholderIcon = usePlaceholderIconOnly();
  const diagnostics = p.diagnostics.filter((d) => d.path.endsWith("email.html")).map((d) => ({ line: d.line ?? null, col: d.column ?? null, message: d.message, severity: "error" as const }));

  // The shared layout is the settings grid's third field; what the choice means (and a way to open the layout) is one
  // compact line under the three fields.
  const layoutField = authoring.layoutBinding ? (
    <Dropdown label={t("messaging.builder.sharedLayout", "Shared layout")} helperText={t("messaging.builder.layout.help", "Only published layouts can be chosen.")}
      options={layoutOptions(layouts, def, work.layout, t)} value={bindingValue(work.layout)} disabled={!p.canEdit}
      onChange={(v) => p.setWork({ ...work, layout: bindingFromValue(String(v)) })} />
  ) : (
    <Field label={t("messaging.builder.sharedLayout", "Shared layout")}>
      <p className="msgb-small">{authoring.unsupportedReasons.layoutBinding || t("messaging.builder.layout.fixed", "This message always uses the global default layout.")}</p>
    </Field>
  );
  const layoutMeaning = authoring.layoutBinding ? (
    <div className="msgb-span">
      <Banner tone={binding.tone} size="compact" title={binding.title}
        action={eff ? <Btn variant="link" size="sm" onClick={() => { if (gateBuilder(BODY_BUILDER_ID).ok) p.onOpenLayout(eff.layoutKey); }}>{t("messaging.builder.openLayout", "Open this layout")}</Btn> : undefined}>
        {binding.text}
      </Banner>
    </div>
  ) : null;

  return (
    <>
      <div className="msgb-edbody">
      {p.lead}
      {/* Inline, not a Drawer: the builder toolbar's Placeholders picker inserts into the subject and preheader. */}
      <Disclosure summary={t("messaging.builder.settings", "Message settings")} accessory={settingsDefault ? undefined : <span className="msgb-small msgb-ellipsis msgb-hide-phone">{work.subject}</span>}
        open={settingsDefault} onToggle={p.setSettingsOpen}>
        <div className="msgb-settings">
          <Field label={t("messaging.builder.subject", "Subject")} required htmlFor={SUBJECT_ID}>
            <Input id={SUBJECT_ID} full value={work.subject} readOnly={!p.canEdit} maxLength={200}
              onChange={(e) => p.setWork({ ...work, subject: e.target.value })}
              onFocus={p.caret("subject")} onSelect={p.caret("subject")} onKeyUp={p.caret("subject")} />
          </Field>
          {authoring.preheader ? (
            <Field label={t("messaging.builder.preheader", "Preheader")} htmlFor={PRE_ID}
              hint={t("messaging.builder.preheader.hint", "The line inboxes show after the subject. Leave it empty if you don't need it.")}>
              <Input id={PRE_ID} full value={work.preheader} readOnly={!p.canEdit} maxLength={200}
                onChange={(e) => p.setWork({ ...work, preheader: e.target.value })}
                onFocus={p.caret("preheader")} onSelect={p.caret("preheader")} onKeyUp={p.caret("preheader")} />
            </Field>
          ) : (
            <Field label={t("messaging.builder.preheader", "Preheader")}>
              <p className="msgb-small">{authoring.unsupportedReasons.preheader || t("messaging.builder.preheader.no", "This message can't have a preheader.")}</p>
            </Field>
          )}
          {layoutField}
          {layoutMeaning}
        </div>
      </Disclosure>

      {p.issuesBanner}
      {p.previewFailure ? <FailureBanner t={t} failure={p.previewFailure} onRetry={p.onPreviewRetry}>{" "}{t("messaging.builder.previewFailed", "The editor keeps working; the server preview will update once this succeeds.")}</FailureBanner> : null}
      {body.unmapped ? <Banner tone="neutral" size="compact" title={t("messaging.builder.sourceOnly", "Source only")}>{t("messaging.builder.sourceOnlyText", "This body can't be shown as blocks. Edit it in the HTML view; your text is kept exactly.")} ({body.unmapped})</Banner> : null}
      {body.stale ? <Banner tone="warning" size="compact" title={t("messaging.builder.paused", "Visual editing is paused")}>{t("messaging.builder.pausedText", "The HTML can't be shown as blocks right now. Fix it in the HTML view; the editor shows the last version it could read.")}</Banner> : null}
      {body.canvasError ? <Banner tone="warning" size="compact" title={t("messaging.builder.canvasError", "The canvas can't render right now")}>{body.canvasError} {t("messaging.builder.canvasErrorText", "Your draft is unchanged; HTML still works.")}</Banner> : null}
      </div>
      {eff && eff.published && !layoutHtml ? <PreviewSkeleton label={t("messaging.builder.loading.layout", "Loading the layout")} height={frame === "flow" ? 480 : undefined} /> : (
        <div className="msgb-builderhost">
          <EmailBuilder
            id={BODY_BUILDER_ID}
            frame={frame}
            density="compact"
            fluidDesktop
            views={TEMPLATE_VIEWS}
            documentKey={p.templateId}
            title={p.message.name}
            readOnly={!p.canEdit}
            srcdoc={body.srcdoc}
            previewSrcdoc={body.previewSrcdoc}
            nodes={body.projection ? (body.projection.nodes as never) : {}}
            doc={body.projection ? body.projection.doc : {}}
            rootIds={body.projection ? body.projection.rootIds : []}
            branches={body.projection ? body.projection.branches : {}}
            selectedId={body.selectedId}
            onSelect={body.onSelect}
            onOp={body.onOp}
            canUndo={body.canUndo} canRedo={body.canRedo} onUndo={body.onUndo} onRedo={body.onRedo}
            device={p.device} deviceWidths={VIEWPORT.widths} onDevice={p.onDevice}
            bodyStyle={body.projection && body.projection.wrap && body.projection.wrap.valid ? body.projection.wrap.style : {}}
            bodyLocked={!!(body.projection && body.projection.wrap && !body.projection.wrap.valid)}
            layoutName={eff ? layoutLabel(eff, t) : t("messaging.builder.layout.none", "No layout")}
            onEditLayout={() => { if (eff && gateBuilder(BODY_BUILDER_ID).ok) p.onOpenLayout(eff.layoutKey); }}
            groups={body.groups}
            groupsNote={body.groups.length ? t("messaging.builder.groupsNote", "Showing one version of each condition. Your choice here is only for viewing; it isn't saved.") : undefined}
            jsonText={body.jsonText}
            downloadName={`${p.detail.messageId.replace(/[^A-Za-z0-9_.-]/g, "_")}-${p.detail.locale || "default"}-email-body.json`}
            targetLabel={`${p.message.name}, ${p.detail.locale || t("messaging.builder.defaultLanguageInline", "default language")}`}
            onImportValidate={body.onImportValidate}
            onImportApply={body.onImportApply}
            linkOptions={linkOptions}
            onEditSource={(id) => {
              if (!gateBuilder(BODY_BUILDER_ID).ok) return;
              const r = body.sourceRange(id);
              p.setSourceOpen(true);
              setTimeout(() => focusSourceEditorRange(SRC_ID, r ? r.start : 0, r ? r.end : 0), 0);
            }}
            onFocusText={() => { p.target.current = { kind: "canvas" }; }}
            sectionsExtra={sectionsExtra}
            previewExtra={<PreviewDataPanel t={t} context={recordContext(p.message.groupId)} value={p.preview} onChange={p.setPreview} rendered={p.rendered} rendering={p.rendering} />}
            toolbarSlot={p.placeholderPicker(!p.canEdit, placeholderIcon)}
            toolbarSourceSlot={p.sourceOpen && snippetOptions.length ? (
              <Dropdown size="sm" portal aria-label={t("messaging.builder.logic.aria", "Insert logic")} placeholder={t("messaging.builder.logic", "Insert logic")}
                options={snippetOptions} value={null} onChange={(v) => insertLogic(String(v))} disabled={!p.canEdit} style={{ width: 200 }} />
            ) : undefined}
            sourceOpen={p.sourceOpen}
            onSourceOpen={(o) => { if (!o || gateBuilder(BODY_BUILDER_ID).ok) p.setSourceOpen(o); }}
            sourcePanel={
              <SourceEditor id={SRC_ID} label={t("messaging.builder.bodyHtml", "Message body HTML")} language="liquid-html"
                docKey={p.templateId} rows={12} maxHeight="100%" value={work.html} readOnly={!p.canEdit}
                onChange={(html) => p.setWork({ ...work, html })}
                onFocus={() => { p.target.current = { kind: "source" }; }}
                diagnostics={diagnostics} invalid={diagnostics.length > 0}
                completions={p.fields.map((f) => ({ id: String(f.id), detail: String(f.label) }))} />
            }
            canAddBlock={p.canEdit && !body.stale && !body.unmapped}
            addBlockHint={!p.canEdit ? t("messaging.builder.readOnly", "Read only") : undefined}
          />
        </div>
      )}
    </>
  );
}

function recordContext(groupId: string) {
  const c = tokenContextFor(groupId);
  return c === "appointment" || c === "event" || c === "queue" ? c : null;
}

/* ======================= SMS ======================= */

interface SmsPaneProps extends TemplateEditorProps {
  /** Row notices shown first (conflict, notices, warnings, placeholder access). */
  lead: ReactNode;
  templateId: string;
  detail: MessageTemplateDetail;
  work: SmsWork;
  setWork: (w: Work) => void;
  canEdit: boolean;
  issuesBanner: ReactNode;
  sms: { text: string; characters: number; segments: number; encoding: "gsm7" | "ucs2" } | null;
  rendering: boolean;
  previewFailure: Failure | null;
  onPreviewRetry: () => void;
  placeholderPicker: (disabled: boolean, iconOnly?: boolean) => ReactNode;
  caret: (kind: "subject" | "preheader" | "sms") => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => void;
  preview: PreviewSource;
  setPreview: (s: PreviewSource) => void;
  rendered: { values: "catalog_samples" | "record"; missingTokens: string[]; unknownTokens: string[] } | null;
}

function SmsPane(p: SmsPaneProps) {
  const { t, work } = p;
  // A refused render keeps the last good result in the read, but that result is for older text: show neither its
  // count nor its bubble as current. The failure and its Retry stand in for them until a render succeeds.
  const sms = p.previewFailure ? null : p.sms;
  const counts = sms
    ? `${sms.characters} ${t("messaging.builder.sms.chars", "characters")} · ${sms.segments} ${sms.segments === 1 ? t("messaging.builder.sms.segment", "message") : t("messaging.builder.sms.segments", "messages")} · ${sms.encoding === "ucs2" ? t("messaging.builder.sms.unicode", "Unicode (shorter messages)") : t("messaging.builder.sms.gsm", "Standard characters")}`
    : null;
  // Only a fresh, successful render may say the text runs past one SMS. While it re-renders, or the read failed,
  // the count line already says so, and this notice stays away rather than restating an old count as current.
  const multipart = !!p.sms && p.sms.segments > 1 && !p.rendering && !p.previewFailure;
  return (
    <>
      <div className="msgb-edbody">
        {p.lead}
        <PanelHeader as="h3" size="sm" divider={false} title={t("messaging.builder.sms.title", "SMS text")}
          description={t("messaging.builder.sms.text", "Plain text only. SMS has no subject, preheader, shared layout, HTML or CSS.")} />
        {p.issuesBanner}
      </div>
      <div className="msgb-two">
        <div className="msgb-stack">
          {/* the placeholder picker sits next to the field it inserts into */}
          <div className="msgb-fieldbar">
            <label htmlFor={SMS_ID} className="msgb-h3">{t("messaging.builder.sms.message", "Message")}</label>
            <div>{p.placeholderPicker(!p.canEdit)}</div>
          </div>
          <Textarea id={SMS_ID} rows={6} autoGrow maxRows={12} value={work.text} readOnly={!p.canEdit} aria-required="true"
            onChange={(e) => p.setWork({ ...work, text: e.target.value })}
            onFocus={p.caret("sms")} onSelect={p.caret("sms")} onKeyUp={p.caret("sms")} aria-describedby="msgb-sms-count" />
          <p className="msgb-small" id="msgb-sms-count" role="status" aria-live="polite">
            {counts ? counts : p.previewFailure ? t("messaging.builder.sms.countUnavailable", "Not counted: the preview for this text failed") : p.rendering ? t("messaging.builder.sms.counting", "Counting…") : ""}
            {counts && p.rendering ? ` · ${t("messaging.builder.sms.updating", "updating")}` : ""}
          </p>
          {multipart ? (
            <Banner tone="info" size="compact" testid="msgb-sms-multipart"
              title={t("messaging.builder.sms.multipart.title", "This preview uses more than one SMS")}>
              {t("messaging.builder.sms.multipart.text", "Longer texts are sent as several SMS messages, which may cost more. Recipient details filled in for placeholders can change the final length.")}
            </Banner>
          ) : null}
          <PreviewDataPanel t={t} context={recordContext(p.message.groupId)} value={p.preview} onChange={p.setPreview} rendered={p.rendered} rendering={p.rendering} />
        </div>
        <section aria-label={t("messaging.builder.sms.preview", "SMS preview")} className="msgb-phone" aria-busy={p.rendering}>
          <span className="msgb-phone-from">{t("messaging.builder.sms.previewLabel", "Preview, rendered by the server")}</span>
          {p.previewFailure ? <FailureBanner t={t} failure={p.previewFailure} onRetry={p.onPreviewRetry}>{" "}{t("messaging.builder.previewFailed", "The editor keeps working; the server preview will update once this succeeds.")}</FailureBanner> : null}
          {sms ? <div className="msgb-bubble" style={{ opacity: p.rendering ? 0.6 : 1 }}>{sms.text}</div>
            : !p.previewFailure ? <div role="status" aria-busy="true"><span className="msgb-sr">{t("messaging.builder.sms.rendering", "Rendering the preview")}</span><Skeleton variant="rect" width="78%" height={64} /></div> : null}
        </section>
      </div>
    </>
  );
}
